import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, MapPin, ClipboardList, Box, Truck, ScanLine,
  ChevronRight, CheckCircle, Clock, AlertTriangle, Zap,
  BarChart3, Users, TrendingUp, Settings, Bell, Sun, Moon, Loader2
} from 'lucide-react';
import { WarehouseCard, WarehouseButton, WarehouseStatsCard, WarehouseBadge, WarehouseProgress } from '../components/WarehouseUIKit';
import { WarehouseBottomNav } from '../components/WarehouseUIKit';
import { useWarehouseTheme } from '../context/WarehouseThemeContext';
import '../styles/warehouse.css';

import {
  receivingAPI, pickingAPI, packingAPI, palletAPI
} from '@/shared/services/api';

const navItems = [
  { id: 'home', label: 'Home', icon: Package, badge: 0 },
  { id: 'tasks', label: 'Tasks', icon: ClipboardList, badge: 0 },
  { id: 'scan', label: 'Scan', icon: ScanLine, badge: 0 },
  { id: 'stats', label: 'Stats', icon: BarChart3, badge: 0 },
  { id: 'profile', label: 'Profile', icon: Users, badge: 0 },
];

export const WarehouseDashboard = () => {
  const navigate = useNavigate();
  const { isDark, setTheme } = useWarehouseTheme();
  const [activeNav, setActiveNav] = useState('home');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0,
    efficiency: 0,
    scansToday: 0
  });
  const [operations, setOperations] = useState([
    { id: 'receiving', name: 'Receiving', description: 'Scan inbound shipments', icon: Package, href: '/mobile/receiving', pending: 0, completed: 0, color: '#2563eb' },
    { id: 'putaway', name: 'Put-away', description: 'Store items in locations', icon: MapPin, href: '/mobile/putaway', pending: 0, completed: 0, color: '#16a34a' },
    { id: 'picking', name: 'Picking', description: 'Pick orders for shipping', icon: ClipboardList, href: '/mobile/picking', pending: 0, completed: 0, color: '#ea580c' },
    { id: 'packing', name: 'Packing', description: 'Pack items into cartons', icon: Box, href: '/mobile/packing', pending: 0, completed: 0, color: '#7c3aed' },
    { id: 'loadout', name: 'Load-out', description: 'Load onto delivery vehicles', icon: Truck, href: '/mobile/loadout', pending: 0, completed: 0, color: '#0891b2' }
  ]);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const [receivingData, pickingData, packingData, palletData] = await Promise.all([
          receivingAPI.getStats().catch(() => ({})),
          pickingAPI.getStats().catch(() => ({})),
          packingAPI.getStats().catch(() => ({})),
          palletAPI.getStats().catch(() => ({}))
        ]);

        // Calculate totals from WMS stats
        const totalPending = (receivingData.pending || 0) + (pickingData.pending || 0) + (packingData.pending || 0);
        const totalInProgress = (receivingData.in_progress || 0) + (pickingData.in_progress || 0) + (packingData.in_progress || 0);
        const totalCompleted = (receivingData.completed_today || 0) + (pickingData.completed_today || 0) + (packingData.completed_today || 0);

        setStats({
          pending: totalPending,
          inProgress: totalInProgress,
          completed: totalCompleted,
          efficiency: totalCompleted > 0 ? Math.round((totalCompleted / (totalCompleted + totalPending)) * 100) : 0,
          scansToday: totalCompleted
        });

        // Update operations with real data
        setOperations(prev => prev.map(op => {
          switch (op.id) {
            case 'receiving': return { ...op, pending: receivingData.pending || 0, completed: receivingData.completed_today || 0 };
            case 'picking': return { ...op, pending: pickingData.pending || 0, completed: pickingData.completed_today || 0 };
            case 'packing': return { ...op, pending: packingData.pending || 0, completed: packingData.completed_today || 0 };
            case 'putaway': return { ...op, pending: receivingData.pending_putaway || 0, completed: 0 };
            case 'loadout': return { ...op, pending: (palletData.pallets_staged || 0), completed: (palletData.pallets_sealed || 0) };
            default: return op;
          }
        }));

        setAlerts([
          { id: 1, type: 'info', message: `${totalPending} tasks pending across all modules`, time: 'Now' }
        ]);

      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  const handleNavClick = (id) => {
    setActiveNav(id);
    if (id === 'scan') {
      navigate('/mobile/scan');
    } else if (id === 'profile') {
      navigate('/mobile/profile');
    }
  };

  if (loading) {
    return (
      <div className="warehouse-app min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--wh-primary)' }} />
      </div>
    );
  }

  return (
    <div className="warehouse-app min-h-screen pb-20">
      {/* Header */}
      <header
        className="wh-safe-top px-4 py-4"
        style={{
          background: isDark ? 'var(--wh-bg-secondary)' : 'var(--wh-primary)',
          color: isDark ? 'var(--wh-text-primary)' : 'white'
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold">EzWarehouse</h1>
            <p className="text-sm opacity-80">Good morning, Operator!</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-full"
              style={{ background: 'rgba(255,255,255,0.1)' }}
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              className="p-2 rounded-full relative"
              style={{ background: 'rgba(255,255,255,0.1)' }}
            >
              <Bell className="w-5 h-5" />
              <span
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs flex items-center justify-center"
              >
                2
              </span>
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div
            className="text-center p-3 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.15)' }}
          >
            <p className="text-2xl font-bold">{stats.pending}</p>
            <p className="text-xs opacity-80">Pending</p>
          </div>
          <div
            className="text-center p-3 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.15)' }}
          >
            <p className="text-2xl font-bold">{stats.inProgress}</p>
            <p className="text-xs opacity-80">In Progress</p>
          </div>
          <div
            className="text-center p-3 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.15)' }}
          >
            <p className="text-2xl font-bold">{stats.completed}</p>
            <p className="text-xs opacity-80">Completed</p>
          </div>
        </div>
      </header>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="px-4 mt-4">
          {alerts.map(alert => (
            <div
              key={alert.id}
              className="flex items-center gap-3 p-3 rounded-xl mb-2"
              style={{
                background: alert.type === 'warning' ? 'var(--wh-warning-muted)' : 'var(--wh-info-muted)',
                borderLeft: `4px solid ${alert.type === 'warning' ? 'var(--wh-warning)' : 'var(--wh-info)'}`
              }}
            >
              <AlertTriangle
                className="w-5 h-5 flex-shrink-0"
                style={{ color: alert.type === 'warning' ? 'var(--wh-warning)' : 'var(--wh-info)' }}
              />
              <div className="flex-1">
                <p className="text-sm" style={{ color: 'var(--wh-text-primary)' }}>{alert.message}</p>
                <p className="text-xs" style={{ color: 'var(--wh-text-tertiary)' }}>{alert.time}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Today's Progress */}
      <div className="px-4 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="wh-text-subtitle">Today&apos;s Progress</h2>
          <span
            className="text-sm font-semibold"
            style={{ color: 'var(--wh-success)' }}
          >
            {stats.efficiency}% Efficiency
          </span>
        </div>
        <WarehouseCard elevated>
          <div className="flex items-center gap-4 mb-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: 'var(--wh-success-muted)' }}
            >
              <Zap className="w-6 h-6" style={{ color: 'var(--wh-success)' }} />
            </div>
            <div className="flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold" style={{ color: 'var(--wh-text-primary)' }}>
                  {stats.scansToday}
                </span>
                <span className="text-sm" style={{ color: 'var(--wh-text-secondary)' }}>scans</span>
              </div>
              <div className="flex items-center gap-1 text-sm" style={{ color: 'var(--wh-success)' }}>
                <TrendingUp className="w-4 h-4" />
                <span>+12% vs yesterday</span>
              </div>
            </div>
          </div>
          <WarehouseProgress
            value={stats.completed}
            max={stats.pending + stats.inProgress + stats.completed}
            label="Daily Target"
          />
        </WarehouseCard>
      </div>

      {/* Operations */}
      <div className="px-4 mt-6">
        <h2 className="wh-text-subtitle mb-3">Operations</h2>
        <div className="space-y-3">
          {operations.map(op => {
            const Icon = op.icon;
            return (
              <WarehouseCard
                key={op.id}
                interactive
                onClick={() => navigate(op.href)}
                className="wh-animate-slide-up"
                data-testid={`op-${op.id}`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center"
                    style={{ background: op.color, boxShadow: `0 4px 12px ${op.color}40` }}
                  >
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg" style={{ color: 'var(--wh-text-primary)' }}>
                      {op.name}
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--wh-text-secondary)' }}>
                      {op.description}
                    </p>
                    <div className="flex gap-4 mt-2">
                      <span className="flex items-center gap-1 text-xs">
                        <Clock className="w-3 h-3" style={{ color: 'var(--wh-warning)' }} />
                        <span style={{ color: 'var(--wh-text-secondary)' }}>{op.pending} pending</span>
                      </span>
                      <span className="flex items-center gap-1 text-xs">
                        <CheckCircle className="w-3 h-3" style={{ color: 'var(--wh-success)' }} />
                        <span style={{ color: 'var(--wh-text-secondary)' }}>{op.completed} done</span>
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5" style={{ color: 'var(--wh-text-tertiary)' }} />
                </div>
              </WarehouseCard>
            );
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="px-4 mt-6 mb-8">
        <h2 className="wh-text-subtitle mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-3">
          <WarehouseButton
            variant="primary"
            fullWidth
            icon={ScanLine}
            onClick={() => navigate('/mobile/scan')}
          >
            Quick Scan
          </WarehouseButton>
          <WarehouseButton
            variant="secondary"
            fullWidth
            icon={ClipboardList}
            onClick={() => navigate('/mobile/picking')}
          >
            My Tasks
          </WarehouseButton>
        </div>
      </div>

      {/* Bottom Navigation */}
      <WarehouseBottomNav
        items={navItems}
        activeItem={activeNav}
        onItemClick={handleNavClick}
      />
    </div>
  );
};

export default WarehouseDashboard;
